import { useEffect, useState } from "react"
import { prefixLibraryLabel, type LibraryItem } from "@/components/library/library-item"
import { LibraryItems, LibraryItemsReporter } from "@/components/library/library-items"
import { LibraryNodeEmpty, LibraryNodeError, LibraryNodeLoading } from "@/components/library/library-node-state"
import { useTranslation } from "@/hooks/use-translation"
import { fetchLibraryFlowFolder, fetchLibraryFlowOrphans, type LibraryFlowGroup } from "@/lib/library-api"
import type { LibraryViewMode } from "@/lib/library-view-mode"

type Props = {
  projectId: number
  flowId?: number
  /** Папка файлов, чья доска уже снята. */
  orphans?: boolean
  mode: LibraryViewMode
  frameRate?: number
  focusedKey?: string | null
  onFocusItem?: (key: string) => void
  onOpenItem?: (item: LibraryItem) => void
  onItemsChange?: (items: LibraryItem[]) => void
}

function groupItems(group: LibraryFlowGroup, frameRate?: number): LibraryItem[] {
  return group.items.map((item) => {
    const video = item.kind === "video"
    // Подпись ноды — бывшая папка. Совпадает с именем файла — не дублируем.
    const label = prefixLibraryLabel(group.label, item.label)
    return {
      key: item.key,
      kind: item.kind,
      label,
      createdAt: item.created_at,
      thumbUrl: item.thumb_url,
      source: item.source,
      asset: {
        title: label,
        kind: item.kind,
        url: video ? null : item.url,
        videoUrl: video ? item.url : null,
        downloadUrl: item.download_url ?? item.url,
        createdAt: item.created_at,
        durationMs: item.duration_ms,
        frameRate: frameRate ?? null,
      },
    }
  })
}

export function LibraryFlowNode({
  projectId,
  flowId,
  orphans = false,
  mode,
  frameRate,
  focusedKey,
  onFocusItem,
  onOpenItem,
  onItemsChange,
}: Props) {
  const { t } = useTranslation(["workspace", "common"])
  const [groups, setGroups] = useState<LibraryFlowGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setFailed(false)
    const request = orphans
      ? fetchLibraryFlowOrphans(projectId, { signal: controller.signal })
      : flowId != null
        ? fetchLibraryFlowFolder(projectId, flowId, { signal: controller.signal })
        : Promise.resolve([] as LibraryFlowGroup[])
    request
      .then((rows) => {
        if (!controller.signal.aborted) setGroups(rows)
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return
        if (error instanceof DOMException && error.name === "AbortError") return
        setFailed(true)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [projectId, flowId, orphans, reloadKey])

  if (loading) return <LibraryNodeLoading />
  if (failed) return <LibraryNodeError onRetry={() => setReloadKey((key) => key + 1)} />
  if (groups.length === 0) return <LibraryNodeEmpty>{t("library.flow.empty")}</LibraryNodeEmpty>

  // Группы не перемешиваем: версии одной ноды уже стоят рядом, от новых к старым.
  const items = groups.flatMap((group) => groupItems(group, frameRate))

  return (
    <>
      <LibraryItemsReporter items={items} onItemsChange={onItemsChange} />
      <LibraryItems
        items={items}
        mode={mode}
        listLayout="table"
        aspectClass="aspect-square"
        focusedKey={focusedKey}
        onFocusItem={onFocusItem}
        onOpenItem={onOpenItem}
      />
    </>
  )
}
